import React, { useState, useEffect, useRef } from 'react';
import { Check, Loader2 } from 'lucide-react';

/**
 * Subtle autosave status indicator.
 * Shows "Saving..." briefly, then "Saved" for a moment, then fades out.
 * Triggers on dependency changes (form data, step, etc.)
 */
export const AutosaveIndicator = ({ dependencies = [], enabled = true }) => {
  const [status, setStatus] = useState('idle'); // idle | saving | saved
  const firstRender = useRef(true);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    // Show "saving" after a short delay (matches autosave timer)
    setStatus('saving');
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setStatus('saved');
      timeoutRef.current = setTimeout(() => setStatus('idle'), 2500);
    }, 2200); // Slightly after autosave fires (2s + network)
    return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  if (status === 'idle' || !enabled) return null;

  return (
    <div
      data-testid="autosave-indicator"
      className={`flex items-center gap-1.5 transition-opacity duration-500 ${status === 'saving' ? 'opacity-60' : 'opacity-100'}`}
    >
      {status === 'saving' ? (
        <>
          <Loader2 className="w-3 h-3 text-stone-400 animate-spin" />
          <span className="text-[10px] text-stone-400 font-medium">Saving...</span>
        </>
      ) : (
        <>
          <Check className="w-3 h-3 text-emerald-500" />
          <span className="text-[10px] text-emerald-600 font-medium">Saved</span>
        </>
      )}
    </div>
  );
};
